// project import
import React, { Suspense, useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import Routes from '@/routes';
import ScrollTop from '@/components/ScrollTop';
import { getAntdTheme } from '@/themes/antd-theme';

import { ChatProvider } from '@/components/FloatingChatPackage/contexts/ChatContext';
import FloatingChat from '@/components/FloatingChatPackage/components/FloatingChat';
import DemoGuide from '@/components/DemoGuide'; // demo-only "?" guide; renders nothing unless localStorage.demoMode === '1'
import { PlantProvider } from '@/contexts/PlantContext';
import { RecipeProvider } from '@/contexts/RecipeContext';
import { check_session_alive } from '@/utils/api';
import PasswordChangeGate from '@/components/PasswordChangeGate'; // PWPOL lane (system-admin F5)

// ==============================|| THEME CONTEXT ||============================== //
export const ThemeContext = createContext({
  isDark: false,
  toggleTheme: () => {},
  fontScale: 'md',
  setFontScale: () => {},
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// ==============================|| APP - THEME, ROUTER, LOCAL  ||============================== //
const loading = (
  <div className="flex items-center justify-center min-h-screen">
    <Spin size="large" tip="Loading..." />
  </div>
);

const App = () => {
  // Font scale: sm | md | lg | xl
  const [fontScale, setFontScale] = useState(() => {
    return localStorage.getItem('fontScale') || 'md';
  });

  useEffect(() => {
    localStorage.setItem('fontScale', fontScale);
    document.documentElement.setAttribute('data-font-scale', fontScale);
  }, [fontScale]);

  // Periodic session check (cookie auth). If THIS session was killed because the
  // same user logged in elsewhere, /bp/session/info returns 401 and the global
  // handler sends us to login — even while idle. Also checks when the tab is
  // refocused so a kicked user is logged out promptly on returning to the tab.
  useEffect(() => {
    check_session_alive();
    const id = setInterval(check_session_alive, 10000);
    const onFocus = () => check_session_alive();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Initialize dark mode from localStorage or system preference
  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Default to light mode when the user hasn't chosen a theme yet (ignore the
    // OS dark preference so the app opens in light mode on first login).
    return false;
  });

  // Update document class and localStorage when theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  // Toggle theme function
  const toggleTheme = () => {
    setIsDark((prev) => !prev);
  };

  // Get theme configuration based on current mode and font scale
  const themeConfig = getAntdTheme(isDark, fontScale);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, fontScale, setFontScale }}>
      <ConfigProvider theme={themeConfig}>
        <BrowserRouter>
          <Suspense fallback={loading}>
            <PlantProvider>
              <RecipeProvider>
                <ScrollTop>
                  <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
                    <Routes />
                    {/* PWPOL lane (system-admin F5): forced change-password modal. At the root so
                        a PASSWORD_CHANGE_REQUIRED answer during the login bootstrap is caught too. */}
                    <PasswordChangeGate />
                    <ChatProvider>
                      <div className="App">
                        <FloatingChat />
                      </div>
                    </ChatProvider>
                    <DemoGuide />
                  </div>
                </ScrollTop>
              </RecipeProvider>
            </PlantProvider>
          </Suspense>
        </BrowserRouter>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export default App;
